import { format } from 'date-fns';
import fhirpath from 'fhirpath';
import jsonpath from 'jsonpath';
import moment from 'moment';
import { WrappedCommunication } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Communication';
import { WrappedDocumentReference } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/DocumentReference';
import { WrappedObservation } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { WrappedServiceRequest } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import {
  ReferralAndRequestGridRowData,
  RequestFilters,
} from 'src/careflow/inck/nat-request/request-type';
import { GridFilters } from 'src/careflow/ph-core/grid/hook/useGridFilters';
import useLocales, {
  i18nFunction,
} from 'src/careflow/ph-core/internationalization/hook/useLocales';
import {
  Bundle,
  CareTeamParticipant,
  CodeableConcept,
  FhirResource,
  Organization,
  OrganizationAffiliation,
  Task,
  TaskInput,
  ValueSetComposeIncludeConcept,
} from 'src/careflow/ph-core/fhir-client/resources';
import { getRegistries } from 'src/careflow/ph-core/multi-tenant/registry/registry';
import { capitalize } from 'src/utils/string';
import { WrappedCondition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import { WrappedGoal } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Goal';
import { WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import {
  CareTeamMemberGridRowData,
  CareTeamStatus,
  GoalGridRowData,
  GroupedGoals,
  Option,
  ServiceRequestGridRowData,
  TaskGridRowData,
  WrappedTaskExtend,
} from 'src/@types/crs/case';
import { referralDtoToRequest } from '../common/common-utils';
import { getSeverityDueDateData } from './getSeverityDueDateData';
import { fhirClient } from 'src/App';
import {
  getFhirIdFromEntity,
  getReferenceId,
  getResourcesAndRelatedResources,
} from 'src/utils/fhir';
import { wrapResource } from 'src/utils/fhirSystem';
import { GoalLifeCycleStatusOption } from 'src/careflow/care-coordination/case/goal/grid/GoalModal';
import { TaskStatus } from 'src/careflow/care-coordination/task/patient/TasksGrid/TaskModal';
import { AlertSeverity, SeverityStatusData } from 'src/components/SeverityStatus';
import { NOTAPPLICABLE } from './constants';
import { ResourceWithIncludedResources } from './types';
import { isValidURL } from 'src/utils/url';
import { OrganizationWrapper } from '../ph-core/fhir-client/fhir-base/wrappers/Organization';

export const searchIfContainedInObj = <T>(
  obj: T,
  searcheableProps: Array<string>,
  valueToSearch: string
): boolean => {
  for (const searcheableProp of searcheableProps) {
    const props = searcheableProp.split('.');
    const propValue = props.reduce<any>((innerProp, prop) => {
      if (!innerProp) {
        innerProp = obj?.[prop as keyof T];
        return innerProp;
      }
      innerProp = innerProp?.[prop];
      return innerProp;
    }, null);
    if (!propValue || typeof propValue !== 'string') continue;
    if (propValue.toLowerCase().includes(valueToSearch)) return true;
  }
  return false;
};

export const getTasksByGoal = (
  goalId: string | undefined,
  tasks: WrappedTask[],
  groupedGoals: GroupedGoals | undefined
): WrappedTask[] =>
  tasks.filter(({ id: taskId }) => {
    if (!goalId || !taskId) return false;
    return groupedGoals?.[goalId].includes(taskId);
  });

export const getConditionsByGoal = (
  goal: WrappedGoal,
  conditions: WrappedCondition[]
): WrappedCondition[] => {
  const addressesIds =
    goal.addresses
      ?.filter((x) => x?.reference?.startsWith('Condition'))
      ?.map((x) => getReferenceResourceId(x?.reference || '')) ?? [];
  if (!addressesIds || addressesIds.length === 0) return [];
  return conditions.filter((x) => x?.id && addressesIds.indexOf(x?.id) > -1);
};

export const getObservationsByGoal = (
  goal: WrappedGoal,
  observations: WrappedObservation[]
): WrappedObservation[] => {
  const addressesIds =
    goal.addresses
      ?.filter((x) => x?.reference?.startsWith('Observation'))
      ?.map((x) => getReferenceResourceId(x?.reference || '')) ?? [];
  if (!addressesIds || addressesIds.length === 0) return [];
  return observations.filter((x) => x?.id && addressesIds.indexOf(x?.id) > -1);
};

export const getParentGoalGridRows = (
  goals: WrappedGoal[],
  tasks: WrappedTask[],
  conditions: WrappedCondition[],
  groupedGoals: GroupedGoals | undefined,
  goalStatuses: GoalLifeCycleStatusOption[],
  observations?: WrappedObservation[]
): GoalGridRowData[] => {
  const parentGoals = goals.filter((goal) => !goal.getParentGoalId());
  return parentGoals.map((parentGoal) =>
    getGoalGridRows(parentGoal, goals, tasks, conditions, groupedGoals, goalStatuses, observations)
  );
};

export const getGoalGridRows = (
  goal: WrappedGoal,
  goals: WrappedGoal[],
  tasks: WrappedTask[],
  conditions: WrappedCondition[],
  groupedGoals: GroupedGoals | undefined,
  goalStatuses: GoalLifeCycleStatusOption[],
  observations?: WrappedObservation[]
): GoalGridRowData => {
  const { target, id, getPlainDescription, getPathway, lifecycleStatus, getPathwayDisplay } = goal;
  const endDateMoment = moment(target?.[0]?.dueDate ?? null, 'YYYY-MM-DD');
  const { severityStatus } = getSeverityDueDateData(endDateMoment);
  const goalStatus = getValueSetConceptValue(goalStatuses ?? [], lifecycleStatus);

  const matchedConditions = getConditionsByGoal(goal, conditions);
  const observationsList = observations ?? [];
  const matchedObservations = getObservationsByGoal(goal, observationsList);

  const subGoals = goals.filter((goal) => goal.getParentGoalId() === id);

  return {
    pathwayDisplay: getPathwayDisplay?.() ?? '',
    pathway: getPathway?.() || '',
    id: id || '',
    endDate: { ...severityStatus, date: endDateMoment },
    status: goalStatus?.display ?? '',
    goal: getPlainDescription?.() ?? '',
    tasks: getTasksByGoal(id, tasks, groupedGoals),
    conditions: [...matchedConditions],
    observations: [...matchedObservations],
    wrappedGoal: goal,
    subGoals: subGoals.length
      ? subGoals.map((goal) =>
          getGoalGridRows(goal, [], tasks, conditions, groupedGoals, goalStatuses, observations)
        )
      : [],
  };
};

export const getAssessmentFromTask = (task: Task) => {
  const inputAssessment =
    task?.input?.find(({ type }) => !!type.coding?.[0]?.code?.includes?.('assessment')) ?? null;
  return inputAssessment;
};

export const isAssessmentReadyToStart = (taskInput: TaskInput) => {
  const assessmentUrl = taskInput?.valueUrl ?? '';

  if (!!isValidURL(assessmentUrl) && assessmentUrl.includes('token=')) {
    return true;
  }
  return false;
};

export interface AssessmentUrlParams {
  taskId?: string;
  patientId?: string;
}

export const getAssessmentUrlWithUserId = (
  taskInput: TaskInput,
  userId?: string,
  params?: AssessmentUrlParams
) => {
  let assessmentUrl = taskInput?.valueUrl ?? '';
  const appendParam = (key: string, value: string) => {
    assessmentUrl += assessmentUrl.includes('?') ? '&' : '?';
    assessmentUrl += `${key}=${encodeURIComponent(value)}`;
  };

  if (!assessmentUrl.includes('userUUID=') && userId) {
    appendParam('userUUID', userId);
  }
  if (params?.taskId) {
    appendParam('taskUUID', params.taskId);
  }
  if (params?.patientId) {
    appendParam('patientUUID', params.patientId);
  }

  return assessmentUrl;
};

export const translateTableHead = (tableHead: any[], i18n: i18nFunction, ns?: any) =>
  tableHead.map((item: any) => {
    const translatedLabel = i18n(item.label, ns);
    return { ...item, label: translatedLabel };
  });

export const translateTableHeadCustom = (
  i18n: (text: any, options?: any) => any,
  tableHead: any[],
  ns?: any
) =>
  tableHead.map((item: any) => {
    const translatedLabel = i18n(item.label, ns);
    return { ...item, label: translatedLabel };
  });

export const getTaskGridRows = (
  tasks: WrappedTaskExtend[],
  taskStatuses: TaskStatus[],
  serviceRequests: WrappedServiceRequest[],
  configurations: any
): TaskGridRowData[] =>
  tasks?.map((task) => {
    const {
      id,
      getDueDate,
      description,
      owner,
      status,
      basedOn,
      code,
      getTaskNumberNAT,
      getInternalNumber,
      dontCollapsible,
      output,
    } = task;
    const endDate = moment(getDueDate() ?? null);
    const { severityStatus: endDateSeverityStatus } = getSeverityDueDateData(endDate);

    // Get requested start date from extension (similar to getDueDate logic)
    const parentExtension = task?.extension?.find(
      (ext) =>
        ext?.url ===
        'https://terminology.patientholistics.app/fhir/StructureDefinition/ph-task-detail'
    );
    const nestedRequestedPeriod = parentExtension?.extension?.find(
      (ext) => ext?.url === 'requestedPeriod'
    );
    const requestedStartDate = nestedRequestedPeriod?.valuePeriod?.start;
    const startDate = moment(requestedStartDate ?? null);
    const { severityStatus: startDateSeverityStatus } = getSeverityDueDateData(startDate);

    const codeCode = code?.coding?.[0]?.code;
    const codeDisplay = code?.coding?.[0]?.display;

    const subTask = task && getTaskBasedOn(`Task/${id}`, tasks);

    const taskStatus = getValueSetConceptValue(taskStatuses ?? [], status);
    const outputServiceRequest = output?.find((e) => e?.valueReference?.type === 'ServiceRequest')
      ?.valueReference?.reference;
    const getServiceRequest =
      serviceRequests?.find((e) => e?.id === getReferenceResourceId(outputServiceRequest || '')) ??
      null;
    const outputAppointmentId = output?.find((e) => e?.valueReference?.type === 'Appointment')
      ?.valueReference?.reference;

    const getTaskNumber = task
      ? getInternalNumber?.(configurations?.taskInternalNumberIdentifierSystem) ??
        getTaskNumberNAT(configurations?.taskNATIdenitifierType) ??
        ''
      : '';

    return {
      id: id ?? '',
      task: description ?? '',
      status: taskStatus?.display ?? '',
      startDate: {
        ...startDateSeverityStatus,
        date: startDate,
      },
      endDate: {
        ...endDateSeverityStatus,
        date: endDate,
      },
      owner: owner?.display ?? '',
      basedOn: basedOn ?? [],
      ...(codeCode && { codeCode: codeCode }),
      ...(codeDisplay && { codeDisplay: codeDisplay }),
      number: getTaskNumber,
      wrappedTask: task,
      ...(dontCollapsible && { dontCollapsible }),
      subTasks: subTask?.map((x) => ({ ...x, dontCollapsible: true })),
      ...(getServiceRequest && { serviceRequest: getServiceRequest }),
      ...(outputAppointmentId && { appointment: getReferenceResourceId(outputAppointmentId) }),
    };
  });

export const getServiceRequestBasedOn = (
  serviceRequestId: string | undefined,
  serviceRequest: WrappedServiceRequest[]
): WrappedServiceRequest[] =>
  serviceRequest?.filter((e) =>
    e.basedOn?.find((s) => s.reference === `ServiceRequest/${serviceRequestId}`)
  );

export const getAnnotations = (
  communications: WrappedCommunication[],
  serviceRequestId: string | undefined
): WrappedCommunication[] =>
  communications?.filter((e) =>
    e.partOf?.some((s) => s.reference === `ServiceRequest/${serviceRequestId}`)
  );

export const getDocuments = (
  documents: WrappedDocumentReference[],
  serviceRequestId: string | undefined
): Number =>
  documents?.filter((d) =>
    d.context?.related?.find((r) => r.reference === `ServiceRequest/${serviceRequestId}`)
  ).length;

export const getServiceRequestGridRows = (
  serviceRequests: WrappedServiceRequest[],
  serviceRequestsChild?: WrappedServiceRequest[]
): ServiceRequestGridRowData[] =>
  serviceRequests.map((serviceRequest) => {
    const { id, code, status, occurrencePeriod, performer, authoredOn, basedOn } = serviceRequest;
    const endDateMoment = moment(occurrencePeriod?.end ?? undefined, 'YYYY-MM-DD');
    const { severityStatus } = getSeverityDueDateData(endDateMoment);
    const child = serviceRequestsChild && getServiceRequestBasedOn(id, serviceRequestsChild);
    return {
      id: id ?? '',
      serviceReferral: code?.coding?.[0].display || '',
      status: status,
      assignedTo: performer?.[0].display || '',
      assignedOn: authoredOn ? format(new Date(authoredOn), 'MMM dd, yyyy') : '',
      dueTo: { ...severityStatus, date: endDateMoment },
      basedOn: basedOn ?? [],
      wrappedServiceRequest: serviceRequest,
      childs: child,
      childCount: child?.length,
    };
  });

export const getTaskBasedOn = (reference: string | undefined, task: WrappedTask[]): WrappedTask[] =>
  task?.filter((e) => e.basedOn?.find((s) => s.reference === reference));

export const getRequestAndReferralGridRows = (
  serviceRequests: any[],
  task: WrappedTask[] | null,
  patient: WrappedPatient | null
): ReferralAndRequestGridRowData[] =>
  serviceRequests?.map((item: any) => {
    const mapServiceRequest = referralDtoToRequest(item);
    const taskChild = task && getTaskBasedOn(item?.fhirId, task);
    const setItem = { ...mapServiceRequest, dob: patient?.birthDate || '', tasks: taskChild ?? [] };
    return setItem;
  });

export const getGoalsIds = (groupedGoals: GroupedGoals | undefined): string | null => {
  if (!groupedGoals) return null;
  return Object.keys(groupedGoals).join(',');
};

export const getTasksIds = (groupedGoals: GroupedGoals | undefined): string | null => {
  if (!groupedGoals) return null;
  return Object.values(groupedGoals)
    .reduce<string[]>((taskIds, tasks) => [...taskIds, ...(tasks || [])], [])
    .join(',');
};

export const getSeverityStatusByCareTeamStatus = (
  status: string | undefined
): SeverityStatusData => {
  const statusSeverityMap: Record<CareTeamStatus, AlertSeverity> = {
    [CareTeamStatus.PROPOSED]: AlertSeverity.WARNING,
    [CareTeamStatus.ACTIVE]: AlertSeverity.SUCCESS,
    [CareTeamStatus.SUSPENDED]: AlertSeverity.ERROR,
    [CareTeamStatus.INACTIVE]: AlertSeverity.DEFAULT,
    [CareTeamStatus.ENTEREDINERROR]: AlertSeverity.ERROR,
  };
  return {
    message: status,
    severity: statusSeverityMap?.[status as CareTeamStatus],
  };
};

export const getOption = <T extends Option<any>>(
  options: T[],
  optionValue: T['value']
): T | null => {
  let option: T | null = null;
  if (optionValue) {
    option = options.find(({ value }) => value === optionValue) as T;
    if (!option) {
      option = {
        label: capitalize(optionValue ?? ''),
        value: optionValue,
      } as T;
    }
  }
  return option;
};

export const getCareTeamMemberGridRows = (
  participants: CareTeamParticipant[],
  participantsWithResource: ResourceWithIncludedResources[]
): CareTeamMemberGridRowData[] =>
  participants?.length
    ? participants.map((participant) => {
        const { member, onBehalfOf, role: participantRole, period } = participant ?? {};
        const startDate = moment(period?.start ?? null);
        const endDate = moment(period?.end ?? null);
        const resourceType = getReferenceResourceType(member?.reference ?? '');
        let associatedOrgName = onBehalfOf?.display ?? '';
        let associatedOrgId = onBehalfOf?.reference ?? null;
        let associatedOrg = associatedOrgName ?? associatedOrgId ?? '';
        const careTeamRole = participantRole?.find((r) =>
          r?.coding?.some((c) => c?.system === 'careTeamRole')
        );
        let roleName =
          careTeamRole?.text ??
          careTeamRole?.coding?.[0]?.display ??
          participantRole?.[0]?.text ??
          participantRole?.[0]?.coding?.[0]?.display ??
          '';
        let roleId =
          careTeamRole?.coding?.[0]?.code ?? participantRole?.[0]?.coding?.[0]?.code ?? null;
        let role = roleName ?? roleId ?? '';
        let resource =
          participantsWithResource?.find(
            ({ resource }) => `${resource?.resourceType}/${resource?.id}` === member?.reference
          ) ?? null;
        const includedResourcesWrapped =
          resource?.includedResources && resource?.includedResources?.length > 0
            ? resource?.includedResources?.map((r) => wrapResource(r))
            : [];

        // Only set associatedOrg for PractitionerRole and CareTeam types
        if (resourceType !== 'PractitionerRole' && resourceType !== 'CareTeam') {
          associatedOrg = NOTAPPLICABLE;
          associatedOrgId = null;
        }

        return {
          id: getReferenceResourceId(member?.reference ?? ''),
          associatedOrg,
          associatedOrgName,
          associatedOrgId,
          role,
          roleName,
          roleId,
          name: member?.display ?? '',
          identifier: member?.identifier?.value ?? '',
          identifierType: member?.identifier?.system ?? '',
          memberType: resourceType,
          startDate: startDate.isValid() ? startDate.format('MMM DD, YYYY') : '',
          endDate: endDate.isValid() ? endDate.format('MMM DD, YYYY') : '',
          startDateObj: startDate.isValid() ? startDate : null,
          endDateObj: endDate.isValid() ? endDate : null,
          member: participant as CareTeamParticipant,
          ...(resource && {
            resource: wrapResource(resource?.resource),
            includedResources: includedResourcesWrapped,
          }),
        };
      })
    : [];

export const getReferenceResourceType = (reference: string): string => reference?.split?.('/')?.[0];
export const getReferenceResourceId = (reference: string): string => reference?.split?.('/')?.[1];

export const getReference = (resourceType: string, id: string): string => `${resourceType}/${id}`;

export const getObjectIfNotExist = <T>(
  options: T[] | null | undefined,
  searchCallback: (resource: T) => boolean,
  objectCreator: () => T
) => {
  const option = options?.find?.(searchCallback);
  if (option) return option;
  return objectCreator();
};

export const getValueSetConceptValue = (
  values: ValueSetComposeIncludeConcept[],
  valueToSearch: string | null | undefined,
  skipCreation: boolean = false
): ValueSetComposeIncludeConcept | null => {
  if (!valueToSearch || !values?.length) return null;

  if (skipCreation) {
    return values.find(({ code }) => code === valueToSearch) || null;
  }

  const concept = getObjectIfNotExist<ValueSetComposeIncludeConcept>(
    values,
    ({ code }) => code === valueToSearch,
    () => ({
      code: valueToSearch,
      display: capitalize(valueToSearch ?? ''),
    })
  );
  return concept;
};
interface Coding {
  coding: Array<{ code: string; display: string; system: string }>;
  text: string;
}

export const getCodeableConceptDisplayName = (data: CodeableConcept, empty: boolean = false): string => {
  if (data?.text) {
    return data.text;
  } else if (data?.coding && data?.coding?.length > 0) {
    return data.coding[0].display ? data.coding[0].display : '';
  } else {
    return empty ? '' : 'No text or coding found';
  }
};

export const getCodingDisplayName = (data: Coding): string => {
  if (data?.text) {
    return data.text;
  } else if (data?.coding && data?.coding?.length > 0) {
    return data.coding[0].display;
  } else {
    return 'No text or coding found';
  }
};

export const filterRequestGridRows = (
  requestGridRows: ReferralAndRequestGridRowData[],
  { filters, searchTextFieldValue }: GridFilters<RequestFilters>
): ReferralAndRequestGridRowData[] => {
  let filteredRequestGridRows = requestGridRows && [...requestGridRows];
  const searchByString = searchTextFieldValue?.toLowerCase().trim() ?? '';
  if (searchByString.length >= 3) {
    filteredRequestGridRows = filteredRequestGridRows?.filter((requestGridRows) =>
      searchIfContainedInObj(requestGridRows, ['number', 'created', 'status'], searchByString)
    );
  }

  const {
    createdDateFilter,
    type,
    statusFilter,
    selectedRequesters,
    selectedPlanDefinitions,
    scope,
    step,
  } = filters;

  return filteredRequestGridRows?.filter((row) => {
    const rowCreatedDate = moment(row?.created ?? null);

    if (createdDateFilter?.isValid?.()) {
      if (!rowCreatedDate?.isValid()) return false;
      if (!rowCreatedDate?.isSame(createdDateFilter, 'day')) return false;
    }

    if (statusFilter?.length) {
      if (!statusFilter?.find((status) => status?.label === row?.status)) return false;
    }

    if (type?.length) {
      if (!type?.find((type) => type?.value === row?.category)) return false;
    }

    if (selectedRequesters?.length) {
      if (
        !selectedRequesters?.find((requester) => requester?.reference === row?.requester?.reference)
      )
        return false;
    }

    if (selectedPlanDefinitions?.length) {
      if (
        !selectedPlanDefinitions?.find((planDefinition) => planDefinition === row?.planDefinition)
      )
        return false;
    }

    if (scope?.length) {
      if (!scope?.find((scope) => scope?.value === row?.scope)) return false;
    }

    if (step !== ('0' as any)) {
      if (step !== (row?.step as any)) return false;
    }

    return true;
  });
};

export const setStatus = (rowStatus: string) => {
  switch (rowStatus) {
    case 'Active':
      return {
        severity: AlertSeverity.SUCCESS,
        message: rowStatus,
      };
    case 'Completed':
      return {
        severity: AlertSeverity.INFO,
        message: rowStatus,
      };
    case 'Revoked':
      return {
        severity: AlertSeverity.ERROR,
        message: rowStatus,
      };
    case 'On Hold':
      return {
        severity: AlertSeverity.WARNING,
        message: rowStatus,
      };
    default:
      return {
        severity: AlertSeverity.DEFAULT,
        message: rowStatus,
      };
  }
};

export const getPredefinedNote = async (
  resource: any,
  groupIdentifier: string,
  keyRegistry: string,
  fhirResource: boolean
) => {
  const data = await getRegistries(groupIdentifier, keyRegistry);
  const dataKeyValue = data?.[0]?.keyValue ? JSON.parse(data?.[0]?.keyValue) : null;

  let customRegistries: any = [];

  if (data) {
    let setVariables: any = [];
    if (dataKeyValue?.variables?.length > 0) {
      setVariables = fhirResource
        ? dataKeyValue?.variables?.map((s: any) => fhirpath.evaluate(resource, s.path))
        : dataKeyValue?.variables?.map((s: any) => jsonpath.query(resource, `$.${s.path}`)[0]);
    }

    customRegistries = dataKeyValue?.messages.map((item: any) => {
      let resultString = item?.display?.replace(
        /\${path\[(\d+)\]}/g,
        (_, index) => setVariables[index]
      );
      return {
        code: item?.code,
        display: resultString,
      };
    });
  }

  return customRegistries ?? null;
};

export function filterTasksByDefinition(
  tasks: WrappedTask[] | undefined,
  activityDefinitions?: string[]
): WrappedTask[] {
  if (!tasks) return [];

  if (!activityDefinitions) return tasks;

  return tasks.filter(
    (t: WrappedTask) =>
      !t.instantiatesCanonical ||
      activityDefinitions!.some(
        (ad: string) => ad === getReferenceResourceId(t.instantiatesCanonical ?? '')
      )
  );
}

export function includeSubtasksOf(
  allTasks: WrappedTask[] | undefined,
  parentTasks: WrappedTask[]
): WrappedTask[] {
  if (!allTasks?.length || !parentTasks?.length) return parentTasks ?? [];
  const parentIds = new Set(parentTasks.map((t) => t?.id).filter(Boolean));
  const subtasks = allTasks.filter((t) =>
    t?.basedOn?.some(
      (ref) => ref?.reference && parentIds.has(getReferenceResourceId(ref.reference))
    )
  );
  const byId = new Map<string, WrappedTask>();
  parentTasks.forEach((t) => t?.id && byId.set(t.id, t));
  subtasks.forEach((t) => t?.id && byId.set(t.id, t));
  return Array.from(byId.values());
}

export const fetchOrganizationAffiliation = async (ids: string[]) => {
  const fhirIds = ids?.join(',') ?? [];
  const bundleOrganizations = await fhirClient.search<OrganizationAffiliation>(
    `OrganizationAffiliation?primary-organization=${fhirIds}&active=true`
  );
  if (!bundleOrganizations?.entry?.length) return [];
  const participatingOrganizations = bundleOrganizations.entry
    .map(({ resource }) => {
      if (
        resource?.resourceType === 'OrganizationAffiliation' &&
        (resource as OrganizationAffiliation).participatingOrganization?.reference
      ) {
        const reference = (resource as OrganizationAffiliation).participatingOrganization!
          .reference!;
        return getFhirIdFromEntity(reference);
      }
      return null;
    })
    .filter((orgId): orgId is string => !!orgId);
  return participatingOrganizations;
};

export const shouldIncludeTaskByChecklistFilters = (
  task: WrappedTask | null | undefined,
  checklistFilter?: {
    listFilter?: string[];
    owner?: string[];
    status?: string[];
  }
): boolean => {
  if (!task) return false;
  if (!checklistFilter) return true;

  const { listFilter = [], owner = [], status = [] } = checklistFilter;

  const hasFilters = listFilter.length || owner.length || status.length;

  if (!hasFilters) return true;

  if (listFilter.length) {
    const codings = task?.code?.coding;
    if (!codings?.length) return false;

    const match = codings.some((c) => listFilter.includes(c?.code ?? ''));

    if (!match) return false;
  }

  if (owner.length) {
    const reference = task?.owner?.reference;
    if (!reference) return false;

    const match = owner.some((o) => reference.includes(o));

    if (!match) return false;
  }

  if (status.length) {
    if (!task.status) return false;

    const match = status.includes(task.status);
    if (!match) return false;
  }

  return true;
};

const isValidActivityDefinition = (
  task: WrappedTask | undefined,
  activityDefinitions?: string[]
): boolean => {
  if (!activityDefinitions?.length) return true;
  if (!task?.instantiatesCanonical) return false;

  return activityDefinitions.some(
    (ad) => ad === getReferenceResourceId(task?.instantiatesCanonical ?? '')
  );
};

export const shouldIncludeTask = (
  row: any,
  filters: {
    checklist?: any;
    activityDefinitions?: string[];
  }
): boolean => {
  const task = row?.wrappedTask;

  return (
    shouldIncludeTaskByChecklistFilters(task, filters.checklist) &&
    isValidActivityDefinition(task, filters.activityDefinitions)
  );
};

export const fetchOrganizationAffiliationByPrimaryOrg = async (id: string) => {
  const response = await fhirClient.search<Bundle>(
    `OrganizationAffiliation?primary-organization=${id}&_include=OrganizationAffiliation:primary-organization&_include=OrganizationAffiliation:participating-organization`
  );

  const resourceEntries =
    response?.entry?.reduce?.<FhirResource[]>((resources, { resource }) => {
      if (!resource) return resources;
      return [...resources, resource];
    }, []) ?? [];

  const [organizationAffiliation, relatedResources] = getResourcesAndRelatedResources(
    resourceEntries,
    'OrganizationAffiliation'
  );

  const mapOrgAffiliation = (organizationAffiliation as OrganizationAffiliation[])?.map(
    (orgAffi) => {
      const primaryFhirResource = orgAffi?.organization?.reference
        ? relatedResources?.find(
            (pOrg) => pOrg?.id === getReferenceId(orgAffi?.organization?.reference)
          )
        : null;

      const participatingFhirResource = orgAffi?.participatingOrganization?.reference
        ? relatedResources?.find(
            (pOrg) => pOrg?.id === getReferenceId(orgAffi?.participatingOrganization?.reference)
          )
        : null;

      const primaryOrg = primaryFhirResource
        ? OrganizationWrapper(primaryFhirResource as Organization)
        : null;

      const participatingOrg = participatingFhirResource
        ? OrganizationWrapper(participatingFhirResource as Organization)
        : null;

      return {
        ...orgAffi,
        primaryOrganization: primaryOrg,
        participatingOrg: participatingOrg,
      };
    }
  );

  return mapOrgAffiliation;
};
